import { useMemo, useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { LegalTabs } from "./LegalTabs";
import { PublishDialog } from "./PublishDialog";
import { Button } from "@/components/Button";
import { SelectField } from "@/components/Field";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { useCollection } from "@/lib/hooks/useCollection";
import { call, CALLABLES } from "@/services/callables";
import { formatDate } from "@/lib/format";
import {
  documentRows,
  filterDocuments,
  publishability,
  sortDocuments,
  STATE_KEY,
  STATE_TONE,
  TYPE_KEY,
  type DocType,
  type DocumentRow,
  type PublishInput,
  type PublishedDocument,
  type RegistryEntry,
} from "./documents";
import type { Country, Locale } from "@/types";

/**
 * The library.
 *
 * Every document the registry knows about, whether or not it has been
 * published. A screen that listed only what is in the database would be a
 * screen on which a document nobody ever published is invisible — and the
 * institutions it applies to would be the ones to discover it, at the moment
 * they tried to start a lesson.
 */
export function LegalPage() {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();

  const registry = useCallable<{ documents: RegistryEntry[] }>(CALLABLES.getLegalRegistry);
  const { rows: published, loading } = useCollection<PublishedDocument>("legalDocuments");

  const [type, setType] = useState<DocType | "">("");
  const [jurisdiction, setJurisdiction] = useState<Country | "">("");
  const [language, setLanguage] = useState<Locale | "">("");
  const [publishing, setPublishing] = useState<DocumentRow | undefined>(undefined);

  const rows = useMemo(
    () => documentRows(registry.data?.documents ?? [], published),
    [registry.data, published]
  );

  const visible = useMemo(
    () => sortDocuments(filterDocuments(rows, { type, jurisdiction, language })),
    [rows, type, jurisdiction, language]
  );

  const types = useMemo(
    () => [...new Set(rows.map((row) => row.entry.type))],
    [rows]
  );
  const jurisdictions = useMemo(
    () => [...new Set(rows.flatMap((row) => row.entry.jurisdictions))].sort(),
    [rows]
  );

  async function publish(input: PublishInput) {
    try {
      const result = await call<PublishInput, { affectedInstitutions: number; notified: number }>(
        CALLABLES.publishLegalDocument,
        input
      );
      success("legal.published", {
        affected: result.affectedInstitutions,
        notified: result.notified,
      });
      setPublishing(undefined);
    } catch (error) {
      showError(error);
    }
  }

  return (
    <SectionShell titleKey="legal.title">
      <LegalTabs />

      <p className="max-w-3xl text-sm text-ink-soft">{t("legal.intro")}</p>

      <div className="mt-6 flex flex-wrap gap-4">
        <SelectField
          labelKey="legal.filter.type"
          value={type}
          onChange={(event) => setType(event.target.value as DocType | "")}
          options={[
            { value: "", labelKey: "legal.filter.all" },
            ...types.map((candidate) => ({ value: candidate, labelKey: TYPE_KEY[candidate] })),
          ]}
        />
        <SelectField
          labelKey="legal.filter.jurisdiction"
          value={jurisdiction}
          onChange={(event) => setJurisdiction(event.target.value as Country | "")}
          options={[
            { value: "", labelKey: "legal.filter.all" },
            ...jurisdictions.map((country) => ({ value: country, labelKey: country })),
          ]}
        />
        <SelectField
          labelKey="legal.filter.language"
          value={language}
          onChange={(event) => setLanguage(event.target.value as Locale | "")}
          options={[
            { value: "", labelKey: "legal.filter.all" },
            { value: "en", labelKey: "common.language.en" },
            { value: "de", labelKey: "common.language.de" },
          ]}
        />
      </div>

      <section className="mt-8">
        {registry.loading || loading ? (
          <p className="text-sm text-ink-muted">{t("common.loading")}</p>
        ) : visible.length === 0 ? (
          <EmptyState titleKey="legal.empty" bodyKey="legal.emptyBody" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t("legal.title")}</caption>
              <thead>
                <tr className="border-b border-paper-deep text-left text-ink-muted">
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("legal.column.document")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("legal.column.type")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("legal.column.jurisdictions")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("legal.column.segments")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("legal.column.version")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("legal.column.state")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("legal.column.published")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">
                    {/*
                      Named, but not shown. A column of buttons with an empty
                      header is a column a screen reader cannot announce, and a
                      visible word above one button per row is noise.
                    */}
                    <span className="sr-only">{t("legal.column.action")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const verdict = publishability(row);
                  return (
                    <tr key={row.entry.id} className="border-b border-paper-warm last:border-0">
                      <td className="px-3 py-3 text-ink">
                        {row.entry.titles[locale] ?? row.entry.titles.en ?? row.entry.id}
                      </td>
                      <td className="px-3 py-3 text-ink-soft">{t(TYPE_KEY[row.entry.type])}</td>
                      <td className="px-3 py-3 text-ink-muted">
                        {row.entry.jurisdictions.join(", ")}
                      </td>
                      <td className="px-3 py-3 text-ink-muted">
                        {row.entry.segments
                          .map((segment) => t(`institutions.segment.${segment}`))
                          .join(", ")}
                      </td>
                      <td className="px-3 py-3 text-ink">
                        {row.published
                          ? t("legal.versionOf", {
                              published: row.published.version,
                              registry: row.entry.version,
                            })
                          : t("legal.versionNone", { registry: row.entry.version })}
                      </td>
                      <td className={`px-3 py-3 ${STATE_TONE[row.state]}`}>
                        {t(STATE_KEY[row.state])}
                      </td>
                      <td className="px-3 py-3 text-ink-muted">
                        {row.published ? formatDate(locale, row.published.publishedAt) : null}
                      </td>
                      <td className="px-3 py-3">
                        <Button
                          variant="secondary"
                          disabled={!verdict.publishable}
                          onClick={() => setPublishing(row)}
                        >
                          {t("legal.publishAction")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {publishing ? (
        <PublishDialog
          row={publishing}
          open
          onClose={() => setPublishing(undefined)}
          onPublish={publish}
        />
      ) : null}
    </SectionShell>
  );
}
